/**
 * Dictionary registry + typed lookup helpers.
 *
 * The German dictionary defines the canonical {@link Dictionary} shape; every
 * other locale must satisfy it (enforced via `satisfies` in each file), so a
 * missing or renamed key is a compile error rather than a runtime blank.
 */
import { DEFAULT_LOCALE } from './config'
import type { Locale } from './config'
import type { Dictionary } from './dictionaries/de'
// Server/test build: all locales. The browser build swaps this module for
// `dictionary-source.client.ts` (English static, the visitor's locale loaded
// before hydration), see `clientOverrides` in vite.config.ts.
import { getDictionarySource } from './dictionary-source'

export type { Dictionary }

export function getDictionary(locale: Locale): Dictionary {
  return getDictionarySource(locale)
}

/**
 * Recursively builds the union of dot-paths into the dictionary that resolve to
 * a `string` (e.g. `'nav.search'`, `'newsletter.title'`). Array/string-array
 * values are addressed by their parent key and read directly via the dictionary
 * object, not through {@link translate}.
 */
type StringPaths<T, TPrefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${TPrefix}${K}`
    : T[K] extends Record<string, unknown>
      ? StringPaths<T[K], `${TPrefix}${K}.`>
      : never
}[keyof T & string]

export type TranslationKey = StringPaths<Dictionary>

export type TranslationValues = Record<string, string | number>

export function interpolate(
  template: string,
  values?: TranslationValues,
): string {
  if (!values) return template
  return template.replace(/\{(\w+)\}/gu, (match, token: string) =>
    token in values ? String(values[token]) : match,
  )
}

/**
 * Resolve a dot-path string key against a dictionary, with `{token}`
 * interpolation. Falls back to the default-locale dictionary, then to the raw
 * key, so a UI never renders `undefined`.
 */
export function translate(
  locale: Locale,
  key: TranslationKey,
  values?: TranslationValues,
): string {
  const resolved =
    resolvePath(getDictionary(locale), key) ??
    resolvePath(getDictionary(DEFAULT_LOCALE), key)
  if (typeof resolved !== 'string') return key
  return interpolate(resolved, values)
}

function resolvePath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, segment) => {
    if (acc && typeof acc === 'object' && segment in acc) {
      return (acc as Record<string, unknown>)[segment]
    }
    return undefined
  }, source)
}
